import { Link } from "react-router-dom";
import { InstagramLogo, FacebookLogo, LinkedinLogo, Phone, MapPin, Shield, ArrowSquareOut, EnvelopeSimple, Star } from "@phosphor-icons/react";

const SHIELD = "/img/brand/sep-shield.webp";

// US LawShield affiliate URL (Secrecy Executive Protection affiliate ID)
const US_LAWSHIELD_URL = "https://lp.uslawshield.com/new-member-sign-up-free-trial/?affid=079ebe86-380f-11f1-b538-06ed5a46dc49";
const US_LAWSHIELD_BANNER = "https://www.uslawshield.com/media/us/30DaysFree/banners/tdfbanner468x60.jpg";

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-black mt-24 relative overflow-hidden" data-testid="site-footer">
      {/* US LawShield Affiliate Section */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#080808] to-black py-14" data-testid="uslawshield-section">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-7">
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-16 h-16 bg-[#FFD300] flex items-center justify-center">
                  <Shield size={32} weight="duotone" className="text-black" />
                </div>
                <div>
                  <div className="overline mb-2 text-[#FFD300]">// LEGAL PROTECTION PARTNER</div>
                  <h3 className="font-heading text-3xl md:text-4xl tracking-tighter leading-[0.95] mb-3">
                    Protected by U.S. LawShield<span className="text-[#FFD300]">®</span>
                  </h3>
                  <p className="text-neutral-300 leading-relaxed">
                    Carrying a firearm is a serious responsibility. <strong className="text-white">U.S. LawShield</strong> is the
                    nation's largest legal-defense program for gun owners — 24/7 attorney-answered emergency hotline,
                    no caps on legal fees, and coverage across covered states. <strong className="text-[#FFD300]">Try it free for 30 days.</strong>
                  </p>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-3">
                    Legal-defense partner · Independent Program Attorneys
                  </p>
                </div>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5">
              <a
                href={US_LAWSHIELD_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="uslawshield-banner-link"
                className="block group lg:ml-auto w-full max-w-[468px] lg:ml-auto mx-auto"
              >
                <div className="border-2 border-[#FFD300]/30 group-hover:border-[#FFD300] transition-colors p-1 bg-white/5">
                  <img
                    src={US_LAWSHIELD_BANNER}
                    alt="Sign up for U.S. LawShield — 30 Days Free Trial"
                    width="468"
                    height="60"
                    className="w-full h-auto block"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 px-1">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400 group-hover:text-[#FFD300] transition-colors flex items-center gap-1.5">
                    <ArrowSquareOut size={10} /> Claim your 30-day free trial
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">SEP partner code</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Training Academy brand banner */}
      <div className="border-b border-white/10 bg-gradient-to-b from-black to-[#0A0A0A] py-14" data-testid="footer-divisions">
        <div className="container-x">
          <Link to="/training" className="group flex items-center gap-6 p-4 border border-white/10 hover:border-[#FFD300] transition-colors max-w-xl mx-auto" data-testid="footer-division-academy">
            <img src="/img/brand/academy.png" alt="Secrecy Executive Training Academy" className="h-24 md:h-28 w-auto flex-shrink-0 drop-shadow-2xl group-hover:scale-105 transition-transform" />
            <div>
              <div className="overline text-[#FFD300] mb-1">TRAINING ACADEMY</div>
              <div className="font-heading text-2xl md:text-3xl tracking-tight leading-tight">Secrecy Executive</div>
              <div className="text-neutral-400 text-sm mt-1">Firearms training · State-certified · Est. 2024</div>
            </div>
          </Link>
        </div>
      </div>

      <div className="container-x py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <img src={SHIELD} alt="SEP" className="h-14 w-auto" />
            <div>
              <div className="font-heading text-lg tracking-tight">SECRECY EXECUTIVE</div>
              <div className="font-mono text-[10px] tracking-[0.28em] text-[#FFD300]">EST. MARYLAND</div>
            </div>
          </div>
          <p className="text-neutral-400 max-w-md text-sm leading-relaxed">
            State-certified firearms & security training academy. Maryland based. Multi-state licensed instructors.
            Law-enforcement-grade mindset. Established 2024.
          </p>
          <div className="mt-6 flex gap-3">
            <a href="https://www.instagram.com/secrecyexecutive" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="p-3 border border-white/10 hover:border-[#FFD300] hover:text-[#FFD300] transition-colors"><InstagramLogo size={18} /></a>
            <a href="https://www.facebook.com/profile.php?id=61592837862489" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="p-3 border border-white/10 hover:border-[#FFD300] hover:text-[#FFD300] transition-colors"><FacebookLogo size={18} /></a>
          </div>
        </div>

        <div>
          <div className="overline mb-4">Navigate</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/training" className="link-gold">Training Academy</Link></li>
            <li><Link to="/elearning" className="link-gold">eLearning Library</Link></li>
            <li><Link to="/about" className="link-gold">About</Link></li>
            <li><Link to="/resources" className="link-gold">Resources</Link></li>
            <li><Link to="/faq" className="link-gold">FAQ</Link></li>
            <li><Link to="/contact" className="link-gold">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li className="flex items-start gap-2"><MapPin size={16} className="mt-1 text-[#FFD300]" /> 4061 Powder Mill Rd, Suite 500-8<br/>Calverton, MD 20705</li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-[#FFD300]" /> <a href="tel:+14106450722" className="hover:text-[#FFD300]">410-645-0722</a></li>
            <li className="flex items-center gap-2"><EnvelopeSimple size={16} className="text-[#FFD300]" /> <a href="mailto:info@seprotection.com" className="hover:text-[#FFD300]">info@seprotection.com</a></li>
            <li className="text-neutral-500 text-xs font-mono tracking-[0.1em] uppercase pl-6">By appointment only</li>
          </ul>
          <div className="mt-6 flex flex-col gap-2">
            <a
              href="https://share.google/kJZoxTVnDC9cUG3Xu"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-[#FFD300] inline-flex items-center gap-1.5"
              data-testid="gbp-profile-link-footer"
            >
              <Star size={11} weight="fill" /> View us on Google <ArrowSquareOut size={9} />
            </a>
            <a
              href={US_LAWSHIELD_URL}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-[#FFD300] inline-flex items-center gap-1.5"
              data-testid="uslawshield-link-footer"
            >
              <Shield size={11} weight="duotone" /> US LawShield Partner <ArrowSquareOut size={9} />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
            © 2026 Secrecy Executive Protection LLC · All Rights Reserved
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy" className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-[#FFD300]" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/refund" className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-[#FFD300]" data-testid="footer-refund-link">Refund Policy</Link>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
              Maryland Lic. · Multi-State Authorized · Lead Instructor: S. Williams
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
